import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as QRCode from 'qrcode';
import * as JSZip from 'jszip'; // 🔧 ADD: JSZip import
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';

export interface InstoreQrDialogData {
    shop: any;
    baseUrl?: string;
}

export interface QRCodeItem {
    tableName: string;
    dataUrl: string;
    qrData: any;
    fullUrl: string;
}

@Component({
    selector: 'app-instore-qr-dialog',
    templateUrl: './instore-qr-dialog.component.html',
    styleUrls: ['./instore-qr-dialog.component.scss'],
    standalone: false
})
export class InstoreQrDialogComponent implements OnInit {
    // QR Type properties
    selectedQRType: 'counter' | 'table' = 'counter';
    tableNumber: string = '';
    
    // Single QR (for counter)
    qrDataUrl: string = '';
    qrJsonData: any = {};
    fullQrUrl: string = '';
    
    // 🔧 NEW: Multiple QR codes (for tables)
    qrCodesArray: QRCodeItem[] = [];
    
    // State properties
    isGenerating: boolean = false;
    errorMessage: string = '';

    constructor(
        public dialogRef: MatDialogRef<InstoreQrDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: InstoreQrDialogData,
        private dialog: MatDialog // 🔧 ADD: MatDialog injection
    ) {}

    ngOnInit(): void {
        // Auto-generate counter QR on init
        this.generateQRCode();
    }

    // 🔧 SIMPLIFIED: Update onQRTypeChange method
    onQRTypeChange(): void {
        // Clear previous data when type changes
        this.clearQRData();
        this.errorMessage = '';
        
        console.log('QR Type changed to:', this.selectedQRType);
        
        // 🔧 AUTO-GENERATE: Generate counter QR automatically
        if (this.selectedQRType === 'counter') {
            // Clear table input when switching to counter
            this.tableNumber = '';
            
            // Auto-generate counter QR
            setTimeout(() => {
                this.performQRGeneration();
            }, 100);
        }
    }

    onTableNumberChange(): void {
        this.errorMessage = '';
        this.clearQRData();
    }

    private clearQRData(): void {
        this.qrDataUrl = '';
        this.qrJsonData = {};
        this.fullQrUrl = '';
        this.qrCodesArray = [];
    }

    // 🔧 UPDATED: Disable table input and button for counter type
    canGenerateQR(): boolean {
        if (this.selectedQRType === 'counter') {
            return true; // Always allow counter generation
        } else {
            return true; // Always allow table generation (even if empty)
        }
    }

    // 🔧 NEW: Check if table input should be disabled
    isTableInputDisabled(): boolean {
        return this.selectedQRType === 'counter';
    }

    // 🔧 NEW: Check if generate button should be disabled
    isGenerateButtonDisabled(): boolean {
        return this.isGenerating || (this.selectedQRType === 'counter');
    }

    // 🔧 UPDATED: Allow download even with empty table numbers
    canPrintOrDownload(): boolean {
        if (this.selectedQRType === 'counter') {
            return !!(this.qrDataUrl && !this.isGenerating && !this.errorMessage);
        } else {
            // 🔧 CHANGED: Allow download even if table number was empty
            return !!(this.qrCodesArray.length > 0 && !this.isGenerating && !this.errorMessage);
        }
    }

    // 🔧 UPDATED: Generate QR with support for multiple tables
    public generateQRCode(): void {
        try {
            if (!this.data || !this.data.shop) {
                this.errorMessage = 'Invalid shop data provided';
                return;
            }

            if (!this.data.shop.code) {
                this.errorMessage = 'Shop code is required for QR generation';
                return;
            }

            // 🔧 NEW: Check if table type with empty input - show confirmation
            if (this.selectedQRType === 'table' && (!this.tableNumber || this.tableNumber.trim().length === 0)) {
                this.showEmptyTableConfirmation();
                return;
            }

            // Proceed with normal generation
            this.performQRGeneration();

        } catch (error) {
            console.error('QR Code generation error:', error);
            this.errorMessage = 'Failed to generate QR code';
            this.isGenerating = false;
        }
    }

    // 🔧 NEW: Show confirmation dialog using existing ConfirmDialogComponent
    private showEmptyTableConfirmation(): void {
        const dialogData = new ConfirmDialogModel(
            'Generate QR Code',
            'Do you want to generate a QR code without a table number?'
        );
        
        // 🔧 CUSTOMIZE: Button text
        dialogData.yesButtonCaption = 'Yes, Generate';
        dialogData.noButtonCaption = 'Cancel';

        const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '400px',
            data: dialogData,
            disableClose: true
        });

        confirmDialogRef.afterClosed().subscribe(result => {
            if (result === true) {
                // console.log('User confirmed generation without table number');
                this.performQRGeneration();
            } else {
                // console.log('User cancelled generation without table number');
                // Reset any loading states if needed
                this.isGenerating = false;
            }
        });
    }

    // 🔧 NEW: Perform the actual QR generation (extracted from original method)
    private performQRGeneration(): void {
        this.isGenerating = true;
        this.errorMessage = '';
        this.clearQRData();

        if (this.selectedQRType === 'counter') {
            this.generateSingleQR('');
        } else {
            this.generateMultipleQRs();
        }
    }

    // 🔧 NEW: Generate single QR for counter
    private generateSingleQR(tableValue: string): void {
        const qrJsonData = {
            mode: "instore",
            origin: "counter-qr",
            shop: this.data.shop.code,
            table: tableValue
        };

        const encodedQrData = encodeURIComponent(JSON.stringify(qrJsonData));
        const baseUrl = this.data.baseUrl || `${window.location.protocol}//${window.location.host}`;
        const fullQrUrl = `${baseUrl}/?qp=${encodedQrData}`;

        QRCode.toDataURL(fullQrUrl, {
            width: 300,
            margin: 2,
            color: {
                dark: '#000000',
                light: '#FFFFFF'
            },
            errorCorrectionLevel: 'M'
        }).then((dataUrl: string) => {
            this.qrDataUrl = dataUrl;
            this.qrJsonData = qrJsonData;
            this.fullQrUrl = fullQrUrl;
            this.isGenerating = false;
        }).catch((error: any) => {
            console.error('QR Code generation failed:', error);
            this.errorMessage = 'Failed to generate QR code';
            this.isGenerating = false;
        });
    }

    // 🔧 NEW: Generate multiple QRs for tables
    private generateMultipleQRs(): void {
        // 🔧 CHANGED: Handle empty or missing table number
        let tableEntries: string[] = [];
        
        if (!this.tableNumber || this.tableNumber.trim().length === 0) {
            // 🔧 EMPTY: Generate one QR with empty table name
            tableEntries = [''];
            // console.log('Generating QR with empty table number');
        } else {
            // Split by comma and process each entry (keep empty ones)
            tableEntries = this.tableNumber
                .split(',')
                .map(name => name.trim()); // Keep empty strings
        }

        // console.log('Table entries to process:', tableEntries);

        // Generate QR for each table entry (including empty ones)
        const qrPromises = tableEntries.map((tableName, index) => {
            // console.log(`Processing entry ${index + 1}: "${tableName}"`);
            return this.generateTableQR(tableName);
        });

        Promise.all(qrPromises).then((qrCodes: QRCodeItem[]) => {
            this.qrCodesArray = qrCodes;
            this.isGenerating = false;
            // console.log(`Successfully generated ${qrCodes.length} QR codes`);
        }).catch((error: any) => {
            // console.error('QR Codes generation failed:', error);
            this.errorMessage = 'Failed to generate QR codes';
            this.isGenerating = false;
        });
    }

    // 🔧 NEW: Generate QR for individual table
    private generateTableQR(tableName: string): Promise<QRCodeItem> {
        return new Promise((resolve, reject) => {
            // 🔧 KEEP: Original tableName (even if empty) for JSON
            const originalTableName = tableName;
            
            const qrJsonData = {
                mode: "instore",
                origin: "table-qr",
                shop: this.data.shop.code,
                table: originalTableName // 🔧 ORIGINAL: Keep empty string if that's what was provided
            };

            const encodedQrData = encodeURIComponent(JSON.stringify(qrJsonData));
            const baseUrl = this.data.baseUrl || `${window.location.protocol}//${window.location.host}`;
            const fullQrUrl = `${baseUrl}/?qp=${encodedQrData}`;

            QRCode.toDataURL(fullQrUrl, {
                width: 200,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                },
                errorCorrectionLevel: 'M'
            }).then((dataUrl: string) => {
                resolve({
                    tableName: originalTableName, // 🔧 ORIGINAL: Keep original value (including empty)
                    dataUrl: dataUrl,
                    qrData: qrJsonData,
                    fullUrl: fullQrUrl
                });
            }).catch(reject);
        });
    }

    // 🔧 UPDATED: Always download as ZIP for both counter and table
    onDownload(): void {
        if (!this.canPrintOrDownload()) {
            this.errorMessage = 'No QR code available for download';
            return;
        }

        try {
            // 🔧 UNIFIED: Always use ZIP download
            this.downloadAsZip();

        } catch (error) {
            console.error('QR Code download error:', error);
            this.errorMessage = 'Failed to download QR code';
        }
    }

    // 🔧 UPDATED: Download both single and multiple QRs as ZIP file
    private async downloadAsZip(): Promise<void> {
        // 🔧 CHECK: Ensure we have QR codes to download
        if (this.selectedQRType === 'counter' && !this.qrDataUrl) {
            this.errorMessage = 'No counter QR code to download';
            return;
        }

        if (this.selectedQRType === 'table' && this.qrCodesArray.length === 0) {
            this.errorMessage = 'No table QR codes to download';
            return;
        }

        try {
            this.isGenerating = true;
            this.errorMessage = '';
            
            const zip = new JSZip();
            const shopCode = this.data.shop.code || 'shop';
            const shopName = this.data.shop.name || 'Unknown Shop';
            
            // 🔧 COUNTER: Handle single counter QR
            if (this.selectedQRType === 'counter') {
                try {
                    // console.log('Processing counter QR code...');
                    
                    // Convert data URL to blob
                    const base64Data = this.qrDataUrl.split(',')[1];
                    const binaryData = atob(base64Data);
                    const arrayBuffer = new ArrayBuffer(binaryData.length);
                    const uint8Array = new Uint8Array(arrayBuffer);
                    
                    for (let j = 0; j < binaryData.length; j++) {
                        uint8Array[j] = binaryData.charCodeAt(j);
                    }
                    
                    // Add counter QR to ZIP
                    const fileName = `counter.png`;
                    zip.file(fileName, uint8Array);
                    
                } catch (error) {
                    console.error('Failed to process counter QR:', error);
                    this.errorMessage = 'Failed to process counter QR code';
                    this.isGenerating = false;
                    return;
                }
            }

            // 🔧 TABLE: Handle multiple table QRs with smart naming for empty tables
            if (this.selectedQRType === 'table') {
                // Create filename map to handle duplicates
                const fileNameCount: { [key: string]: number } = {};
                
                for (let i = 0; i < this.qrCodesArray.length; i++) {
                    const qrCode = this.qrCodesArray[i];
                    
                    try {
                        // 🔧 SMART NAMING: Handle empty names and duplicates
                        let baseName = qrCode.tableName || 'table';
                        let fileName: string;
                        
                        if (fileNameCount[baseName]) {
                            fileNameCount[baseName]++;
                            fileName = `${baseName}_${fileNameCount[baseName]}.png`;
                        } else {
                            fileNameCount[baseName] = 1;
                            fileName = `${baseName}.png`;
                        }
                        
                        // console.log(`Processing ${i + 1}/${this.qrCodesArray.length}: ${fileName} (original: "${qrCode.tableName}")`);
                        
                        // Convert data URL to blob
                        const base64Data = qrCode.dataUrl.split(',')[1];
                        const binaryData = atob(base64Data);
                        const arrayBuffer = new ArrayBuffer(binaryData.length);
                        const uint8Array = new Uint8Array(arrayBuffer);
                        
                        for (let j = 0; j < binaryData.length; j++) {
                            uint8Array[j] = binaryData.charCodeAt(j);
                        }
                        
                        // Add to ZIP
                        zip.file(fileName, uint8Array);
                        
                    } catch (error) {
                        console.error(`Failed to process QR code ${i + 1}:`, error);
                        // Continue with other files
                    }
                }
            }

            // console.log('Generating ZIP file...');
            
            // Generate ZIP with progress callback
            const zipBlob = await zip.generateAsync({ 
                type: 'blob',
                compression: 'DEFLATE',
                compressionOptions: { level: 6 }
            });
            
            // 🔧 FILENAME: Different names based on type
            let fileName: string;
            if (this.selectedQRType === 'counter') {
                fileName = `qr-code-${shopCode}-counter.zip`;
            } else {
                fileName = `qr-codes-${shopCode}-${this.qrCodesArray.length}-tables.zip`;
            }
            
            // Download
            const link = document.createElement('a');
            link.href = URL.createObjectURL(zipBlob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Cleanup
            URL.revokeObjectURL(link.href);
            this.isGenerating = false;

            // console.log('ZIP download completed successfully');

            // 🔧 CLOSE: Dialog with appropriate data
            // this.dialogRef.close({ 
            //     action: 'download', 
            //     shop: this.data.shop, 
            //     qrType: this.selectedQRType,
            //     downloadType: 'zip',
            //     ...(this.selectedQRType === 'table' && { tableCount: this.qrCodesArray.length })
            // });

        } catch (error) {
            console.error('ZIP download error:', error);
            this.errorMessage = 'Failed to create ZIP file. Please try again.';
            this.isGenerating = false;
        }
    }
    // 🔧 REMOVE: No longer needed since we always use ZIP
    // private downloadSingleQR(): void {
    //     // This method is no longer needed
    // }

    // 🔧 UPDATED: Download button text
    getDownloadButtonText(): string {
        if (this.selectedQRType === 'counter') {
            return 'Download ZIP';
        } else if (this.selectedQRType === 'table') {
            if (this.qrCodesArray.length > 1) {
                return `Download ZIP (${this.qrCodesArray.length} files)`;
            } else if (this.qrCodesArray.length === 1) {
                return 'Download ZIP';
            } else {
                return 'Download ZIP';
            }
        }
        return 'Download ZIP';
    }

    // 🔧 ADD: Missing onClose method
    onClose(): void {
        this.dialogRef.close();
    }

    // 🔧 ALTERNATIVE: Close with data
    onCloseWithData(): void {
        this.dialogRef.close({ 
            action: 'close', 
            shop: this.data.shop 
        });
    }
  }
